import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import { POST_TYPE_META, type PostType } from "@/hooks/use-post-generation";

export interface PromptTemplate {
  id: string;
  postType: string;
  brandId: string | null;
  name: string;
  version: number;
  template: string;
  modelName: string | null;
  active: boolean;
  notes: string | null;
  createdBy: string;
  createdAt: string;
  archivedAt: string | null;
}

export interface ExperimentVariant {
  id: string;
  experimentId: string;
  templateId: string;
  label: string;
  weight: number;
  modelOverride: string | null;
  template?: PromptTemplate;
}

export interface PromptExperiment {
  id: string;
  postType: string;
  brandId: string | null;
  name: string;
  status: "DRAFT" | "RUNNING" | "PAUSED" | "CONCLUDED";
  startedAt: string | null;
  endedAt: string | null;
  conclusion: string | null;
  createdAt: string;
  variants: ExperimentVariant[];
}

/**
 * Registry metadata for a prompt key that isn't a PostType (e.g. VIDEO_BRIEF);
 * null on PostType rows.
 */
export interface NamedPromptMeta {
  label: string;
  description: string;
  variables: string[];
  requiredPlaceholders: string[];
  bootstrapTemplate: string;
}

/** Placeholders a template for this row must keep: `{brief}` for PostTypes. */
export function requiredPlaceholders(named: NamedPromptMeta | null | undefined): string[] {
  return (named ? named.requiredPlaceholders : ["brief"]).map((p) => `{${p}}`);
}

/** Display name for a registry row: the named prompt's label, or "Platform · PostType label". */
export function promptTitle(key: string, named: NamedPromptMeta | null | undefined): string {
  if (named) return named.label;
  const meta = POST_TYPE_META[key as PostType];
  return meta ? `${meta.platform} · ${meta.label}` : key;
}

/** Keys of the named (non-PostType) rows in a prompt listing, in API order. */
export function namedPromptKeys(overview: Record<string, { named: NamedPromptMeta | null }>): string[] {
  return Object.keys(overview).filter((k) => overview[k]?.named);
}

export interface PromptOverview {
  postType: string;
  activeScope: "experiment" | "brand" | "global" | "bootstrap";
  template: PromptTemplate | null;
  override: PromptTemplate | null;
  global: PromptTemplate | null;
  experiment: PromptExperiment | null;
  named: NamedPromptMeta | null;
}

export interface PreviewResult {
  text: string;
  parsed: unknown;
  schemaOk: boolean;
  schemaError: string | null;
  usage: { inputTokens: number; outputTokens: number };
  costUsd: string;
  durationMs: number;
  modelName: string;
}

export function usePrompts(brandId: string) {
  return useQuery({
    queryKey: ["prompts", brandId],
    queryFn: () =>
      apiFetch<Record<string, PromptOverview>>(`/brands/${brandId}/prompts`),
    enabled: !!brandId,
  });
}

export function usePromptVersions(brandId: string, postType: string) {
  return useQuery({
    queryKey: ["prompts", brandId, postType, "versions"],
    queryFn: () =>
      apiFetch<PromptTemplate[]>(
        `/brands/${brandId}/prompts/${postType}/versions`,
      ),
    enabled: !!brandId && !!postType,
  });
}

export function useSavePrompt(brandId: string, postType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { template: string; modelName?: string | null; notes?: string | null }) =>
      apiFetch<PromptTemplate>(`/brands/${brandId}/prompts/${postType}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts", brandId] });
      qc.invalidateQueries({ queryKey: ["prompts", brandId, postType, "versions"] });
    },
  });
}

export function useResetPromptOverride(brandId: string, postType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch(`/brands/${brandId}/prompts/${postType}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["prompts", brandId] }),
  });
}

export function useRestorePromptVersion(brandId: string, postType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiFetch<PromptTemplate>(
        `/brands/${brandId}/prompts/${postType}/restore/${versionId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["prompts", brandId] });
      qc.invalidateQueries({ queryKey: ["prompts", brandId, postType, "versions"] });
    },
  });
}

export function usePreviewPrompt(brandId: string, postType: string) {
  return useMutation({
    mutationFn: (body: {
      template: string;
      modelName?: string;
      brief: string;
      voice?: string;
      assets?: string;
      subreddit?: string;
    }) =>
      apiFetch<PreviewResult>(`/brands/${brandId}/prompts/${postType}/preview`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });
}

// ─── Global prompts (org-owner-only) ───────────────────────────────────

export interface GlobalPromptOverview {
  postType: string;
  activeScope: "global" | "bootstrap";
  template: PromptTemplate | null;
  named: NamedPromptMeta | null;
}

export function useGlobalPrompts() {
  return useQuery({
    queryKey: ["global-prompts"],
    queryFn: () =>
      apiFetch<Record<string, GlobalPromptOverview>>(`/admin/prompts/global`),
  });
}

export function useGlobalPromptVersions(postType: string) {
  return useQuery({
    queryKey: ["global-prompts", postType, "versions"],
    queryFn: () =>
      apiFetch<PromptTemplate[]>(`/admin/prompts/global/${postType}/versions`),
    enabled: !!postType,
  });
}

export function useSaveGlobalPrompt(postType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { template: string; modelName?: string | null; notes?: string | null }) =>
      apiFetch<PromptTemplate>(`/admin/prompts/global/${postType}`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["global-prompts"] });
      qc.invalidateQueries({ queryKey: ["global-prompts", postType, "versions"] });
    },
  });
}

export function useRestoreGlobalPromptVersion(postType: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      apiFetch<PromptTemplate>(
        `/admin/prompts/global/${postType}/restore/${versionId}`,
        { method: "POST" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["global-prompts"] });
      qc.invalidateQueries({ queryKey: ["global-prompts", postType, "versions"] });
    },
  });
}

export function useExperiments(brandId: string) {
  return useQuery({
    queryKey: ["experiments", brandId],
    queryFn: () =>
      apiFetch<PromptExperiment[]>(`/brands/${brandId}/experiments`),
    enabled: !!brandId,
  });
}

export function useCreateExperiment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      postType: string;
      name: string;
      variants: Array<{ templateId: string; label: string; weight: number; modelOverride?: string | null }>;
    }) =>
      apiFetch<PromptExperiment>(`/brands/${brandId}/experiments`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["experiments", brandId] }),
  });
}

export function useExperimentAction(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: "start" | "pause" | "conclude"; body?: any }) =>
      apiFetch(`/brands/${brandId}/experiments/${id}/${action}`, {
        method: "POST",
        body: body ? JSON.stringify(body) : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["experiments", brandId] });
      qc.invalidateQueries({ queryKey: ["prompts", brandId] });
    },
  });
}

export interface ExperimentStats {
  experiment: PromptExperiment;
  stats: Array<{
    variantLabel: string;
    runs: number;
    errors: number;
    avgCostUsd: string | null;
    avgDurationMs: number | null;
    avgEvalScore: string | null;
    totalInputTokens: number | null;
    totalOutputTokens: number | null;
  }>;
}

export function useExperimentStats(brandId: string, experimentId: string | null) {
  return useQuery({
    queryKey: ["experiment-stats", brandId, experimentId],
    queryFn: () =>
      apiFetch<ExperimentStats>(`/brands/${brandId}/experiments/${experimentId}/stats`),
    enabled: !!brandId && !!experimentId,
    refetchInterval: 30_000,
  });
}
